import { useCallback } from 'react';
import { useDeleteFeature, useUpdateFeature } from '@/features/features/hooks/features';
import { useTaggableTableRowActions } from '@/features/shared/hooks/useTaggableTableRowActions';
import type { TagColor } from '@/shared/data/tags';
import type { FeatureDto } from '@flowqa/shared';

type UseFeaturesTableRowActionsParams = { selectedProjectId: string | null };

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseFeaturesTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useFeaturesTableRowActions = ({
  selectedProjectId,
}: UseFeaturesTableRowActionsParams): UseFeaturesTableRowActionsResult => {
  const deleteFeatureMutation = useDeleteFeature();
  const updateFeatureMutation = useUpdateFeature();
  const updateRowTags = useCallback(
    async ({ row, tags }: { row: FeatureDto; tags: string[] }) => {
      await updateFeatureMutation.mutateAsync({
        id: row.id,
        projectId: row.projectId,
        tags,
      });
    },
    [updateFeatureMutation],
  );

  return useTaggableTableRowActions({
    deleteMutation: deleteFeatureMutation,
    entity: 'feature',
    selectedProjectId,
    updateRowTags,
    updateRowTagsPending: updateFeatureMutation.isPending,
  });
};
